import { createContext } from "react";
import { createDispatchHook, createSelectorHook, createStoreHook } from "react-redux";
import { buildCreateApi, coreModule, fetchBaseQuery, reactHooksModule } from "@reduxjs/toolkit/query/react";
import { configureStore } from "@reduxjs/toolkit";
//#region src/amBe/amBeApi.ts
const getConfig = (extra) => {
	const config = extra?.amBeApiConfig;
	if (!config) throw new Error("amBeApi: no config found. Create the store with createAmBeApiStore.");
	return config;
};
const fetchWithToken = fetchBaseQuery({
	baseUrl: "",
	prepareHeaders: (headers, { extra }) => {
		const token = getConfig(extra).getAccessToken();
		if (token) headers.set("Authorization", `Bearer ${token}`);
		return headers;
	}
});
const amBeBaseQuery = async (args, api, extraOptions) => {
	const config = getConfig(api.extra);
	const baseUrl = config.getBaseUrl();
	const result = await fetchWithToken(typeof args === "string" ? baseUrl + args : {
		...args,
		url: baseUrl + args.url
	}, api, extraOptions);
	if (result.error) {
		if (extraOptions?.hideErrorNotification !== true) config.onError?.(result.error, api.endpoint);
		return result;
	}
	if (extraOptions?.dataSchema) extraOptions.dataSchema.parse(result.data);
	return result;
};
/**
* The React context the am-be API hooks read the store from.
*/
const AmBeApiContext = createContext(null);
const createAmBeApi = buildCreateApi(coreModule(), reactHooksModule({ hooks: {
	useDispatch: createDispatchHook(AmBeApiContext),
	useSelector: createSelectorHook(AmBeApiContext),
	useStore: createStoreHook(AmBeApiContext)
} }));
const amBeQueryTags = {
	activities: "Activities",
	activityImports: "ActivityImports",
	activityCreateTemplates: "ActivityCreateTemplates"
};
/**
* The base am-be API. Endpoints are added with `amBeApi.injectEndpoints` in
* `./endpoints`.
*/
const amBeApi = createAmBeApi({
	reducerPath: "amBeApi",
	invalidationBehavior: "immediately",
	baseQuery: amBeBaseQuery,
	tagTypes: Object.values(amBeQueryTags),
	endpoints: () => ({})
});
//#endregion
//#region src/amBe/store.ts
/**
* A small store that holds only the am-be API cache.
*
* Create one per app and provide it with
* `<Provider context={AmBeApiContext} store={store}>`. Everything rendered
* below it, including embedded libraries such as te-prefs-lib, then shares one
* cache.
*/
const createAmBeApiStore = (config) => {
	const extraArgument = { amBeApiConfig: config };
	return configureStore({
		reducer: { [amBeApi.reducerPath]: amBeApi.reducer },
		middleware: (getDefaultMiddleware) => getDefaultMiddleware({ thunk: { extraArgument } }).concat(amBeApi.middleware)
	});
};
//#endregion
//#region src/amBe/urlSafe.ts
const objectIdPattern = /^[a-fA-F0-9]{24}$/;
/** Returns `value` if it is a Mongo ObjectId string, so it is safe in a URL path. */
const assertObjectId = (value) => {
	if (typeof value === "string" && objectIdPattern.test(value)) return value;
	throw new Error(`Expected an ObjectId string, but got ${String(value)}`);
};
const useGetActivityByIdQuery = amBeApi.injectEndpoints({ endpoints: (builder) => ({ 
/** One activity by its id. am-be reads the organization from the token. */
getActivityById: builder.query({
	query: ({ organizationId, activityId }) => `organization/${assertObjectId(organizationId)}/activities/${assertObjectId(activityId)}`,
	transformResponse: (response) => response.activity,
	providesTags: (_result, _error, { activityId }) => [{
		type: amBeQueryTags.activities,
		id: activityId
	}]
}) }) }).useGetActivityByIdQuery;
const useGetActivityImportByIdQuery = amBeApi.injectEndpoints({ endpoints: (builder) => ({ getActivityImportById: builder.query({
	query: ({ organizationId, activityImportId }) => `organization/${assertObjectId(organizationId)}/activity-imports/${assertObjectId(activityImportId)}`,
	transformResponse: (response) => response.activityImport,
	providesTags: (_result, _error, { activityImportId }) => [{
		type: amBeQueryTags.activityImports,
		id: activityImportId
	}]
}) }) }).useGetActivityImportByIdQuery;
const useGetActivityCreateTemplateByIdQuery = amBeApi.injectEndpoints({ endpoints: (builder) => ({ 
/** The template an activity import creates its activities from. */
getActivityCreateTemplateById: builder.query({
	query: ({ organizationId, activityCreateTemplateId }) => `organization/${assertObjectId(organizationId)}/activity-create-template/${assertObjectId(activityCreateTemplateId)}`,
	providesTags: (_result, _error, { activityCreateTemplateId }) => [{
		type: amBeQueryTags.activityCreateTemplates,
		id: activityCreateTemplateId
	}]
}) }) }).useGetActivityCreateTemplateByIdQuery;
//#endregion
export { AmBeApiContext, amBeApi, amBeQueryTags, createAmBeApiStore, useGetActivityByIdQuery, useGetActivityCreateTemplateByIdQuery, useGetActivityImportByIdQuery };

//# sourceMappingURL=index.mjs.map